import type { Patrol } from '@local/common-lib';
import { bbox, helpers } from '@turf/turf';
import type { FeatureCollection, Point } from 'geojson';
import { jsonDateParser } from 'json-date-parser';
import type { LngLat } from 'mapbox-gl';
import { useMemo, useState } from 'react';
import { ulid } from 'ulid';
import { useAxios } from '../../hooks/use-axios.js';
import type { Bounds } from '../../utils/geo.js';
import { bboxToBounds } from '../../utils/geo.js';

const incidentColor = (incident: Patrol.Incident): string => {
  switch (incident.what) {
    case 'Accident':
      return '#ea580c';
    case 'Alarm activated':
      return '#ea580c';
    case 'Fire':
      return '#ea580c';
    case 'Graffiti':
      return '#ea580c';
    case 'Medical assistance':
      return '#ea580c';
    case 'Requested checkup/visit':
      return '#16a34a';
    default:
      return '#303030';
  }
};

const incidentSymbol = (incident: Patrol.Incident): string | undefined => {
  switch (incident.what) {
    case 'Accident':
      return 'accident';
    case 'Alarm activated':
      return 'alarm-activated';
    case 'Dodgy people looking shifty':
      return 'people';
    case 'Fire':
      return 'fire';
    case 'Graffiti':
      return 'graffiti';
    case 'Location':
      return undefined;
    case 'Medical assistance':
      return 'medical-assistance';
    case 'Requested checkup/visit':
      return 'requested-checkup';
    case 'Site check':
      return 'site-check';
    case 'Vehicle check':
      return 'vehicle-check';
    default:
      return undefined;
  }
};

export interface WithIncidentProps {
  bounds: Bounds | undefined;
  onClick?: (latLng: LngLat, props: Record<string, unknown>) => void;
  onUnselect?: () => void;
  points: FeatureCollection<Point, Record<string, unknown>>;
  selected?: Patrol.Incident & { _instance: string; patrolId: string };
}

export const withIncidents =
  <P extends object>(
    Component: React.ComponentType<P & WithIncidentProps>,
  ): React.FC<P> =>
  (props) => {
    const [incidents] = useAxios<(Patrol.Incident & { patrolId: string })[]>(
      '/api/v1/incidents/recent',
    );
    const [selected, setSelected] = useState<
      Patrol.Incident & { _instance: string; patrolId: string }
    >();

    const { bounds: tmpBounds, points } = useMemo((): {
      bounds: Bounds | undefined;
      points: FeatureCollection<Point, Record<string, unknown>>;
    } => {
      if (!incidents.data) {
        return { bounds: undefined, points: helpers.featureCollection([]) };
      }
      const pts = helpers.featureCollection(
        incidents.data
          .filter(
            (incident) =>
              !!(incident.location?.longitude && incident.location.latitude),
          )
          .map((incident) =>
            helpers.point(
              // biome-ignore lint/style/noNonNullAssertion: already asserted
              [incident.location!.longitude!, incident.location!.latitude!],
              {
                color: incidentColor(incident),
                incident,
                symbol: incidentSymbol(incident),
              },
            ),
          ),
      );
      const newBounds = pts.features.length
        ? bboxToBounds(bbox(pts))
        : undefined;
      return { bounds: newBounds, points: pts };
    }, [incidents]);

    const bounds = useMemo(() => tmpBounds, [tmpBounds]);

    const onClick = (_latLng: LngLat, props: Record<string, unknown>): void => {
      const incidentProp = props.incident;
      if (typeof incidentProp !== 'string') {
        setSelected(undefined);
        return;
      }
      const incident = JSON.parse(
        incidentProp,
        jsonDateParser,
      ) as Patrol.Incident & { patrolId: string };
      setSelected({ ...incident, _instance: ulid() });
    };

    return (
      <Component
        bounds={bounds}
        onClick={onClick}
        onUnselect={() => setSelected(undefined)}
        points={points}
        selected={selected}
        {...props}
      />
    );
  };
